import React, { useEffect, useState } from 'react';
import AppToast from './AppToast';
import { managementAPI } from '../services/api';

const OwnerSupervision = ({ selectedCompanyId }) => {
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteEmployeesBySite, setSiteEmployeesBySite] = useState({});
  const [supervisorAssignmentsBySite, setSupervisorAssignmentsBySite] = useState({});
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [supervisorAssignForm, setSupervisorAssignForm] = useState({
    factory_id: '',
    supervisor_user_id: '',
    employee_user_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const loadData = async (companyIdParam) => {
    const companyId = companyIdParam || selectedCompanyId || '';
    if (!companyId) {
      setEmployees([]);
      setSites([]);
      setSiteEmployeesBySite({});
      setSupervisorAssignmentsBySite({});
      return;
    }

    const res = await managementAPI.employeesPageBundle(Number(companyId));
    const bundle = res.data || {};
    setEmployees(bundle.employees || []);
    setSites(bundle.sites || []);
    setSiteEmployeesBySite(bundle.factory_employees_by_site || {});
    setSupervisorAssignmentsBySite(bundle.supervisor_assignments_by_site || {});
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadData(selectedCompanyId);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'No se pudo cargar la informaci?n');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!sites.length) {
      setSelectedSiteId('');
      return;
    }
    const exists = sites.some((s) => String(s.id) === String(selectedSiteId));
    if (!exists) {
      const first = String(sites[0].id);
      setSelectedSiteId(first);
      setSupervisorAssignForm((f) => ({
        ...f,
        factory_id: first,
        supervisor_user_id: '',
        employee_user_id: '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites]);

  const getSiteEmployeeOptions = (siteId) => {
    const rows = siteEmployeesBySite[String(siteId)] || [];
    return rows
      .map((r) => {
        const u = employees.find((e) => Number(e.user_id) === Number(r.user_id));
        return {
          ...r,
          name: u?.name || `Usuario ${r.user_id}`,
          email: u?.email || '',
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const roleForSiteRow = (row) => {
    const u = employees.find((e) => Number(e.user_id) === Number(row.user_id));
    return u?.role || 'employee';
  };

  const selectedSiteRows = selectedSiteId ? getSiteEmployeeOptions(selectedSiteId) : [];
  const supervisorCandidates = selectedSiteRows.filter(
    (r) => r.active && roleForSiteRow(r) === 'supervisor'
  );
  const employeeCandidates = selectedSiteRows.filter(
    (r) => r.active && roleForSiteRow(r) === 'employee'
  );
  const rowsForSite = selectedSiteId ? supervisorAssignmentsBySite[String(selectedSiteId)] || [] : [];

  const saveSupervisorAssignment = async (formOverride) => {
    const payloadForm = formOverride || supervisorAssignForm;
    if (!payloadForm.factory_id || !payloadForm.supervisor_user_id || !payloadForm.employee_user_id) {
      return;
    }
    try {
      setErr('');
      setMsg('');
      await managementAPI.createSupervisorAssignment({
        factory_id: Number(payloadForm.factory_id),
        supervisor_user_id: Number(payloadForm.supervisor_user_id),
        employee_user_id: Number(payloadForm.employee_user_id),
        active: true,
      });
      await loadData(selectedCompanyId);
      setMsg('Asignación supervisor-empleado guardada');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo guardar la asignaci?n');
    }
  };

  const removeSupervisorAssignment = async (siteId, assignmentId) => {
    try {
      setErr('');
      setMsg('');
      await managementAPI.deleteSupervisorAssignment(assignmentId);
      await loadData(selectedCompanyId);
      const siteName = sites.find((s) => Number(s.id) === Number(siteId))?.name || 'sede';
      setMsg(`Asignación eliminada en ${siteName}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo eliminar la asignaci?n');
    }
  };

  return (
    <div className="space-y-4">
      <div className="card-mobile">
        <h2 className="text-lg font-semibold text-gray-900">Relaciones de supervisión</h2>
        <p className="text-sm text-gray-500 mt-1">
          Elegí una sede y vinculá supervisores con empleados que trabajan en esa misma sede.
        </p>
      </div>

      {!selectedCompanyId ? (
        <div className="card-mobile">
          <p className="text-sm text-gray-500">Seleccioná una empresa en el encabezado para gestionar supervisión.</p>
        </div>
      ) : sites.length === 0 ? (
        <div className="card-mobile">
          <p className="text-sm text-gray-500">No hay sedes en esta empresa. Creá sedes desde Configuración.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,280px)_minmax(0,1fr)] gap-4 items-start">
          <div className="rounded-xl border border-slate-200 p-3">
            <h3 className="font-semibold mb-2">Sedes</h3>
            <div className="space-y-2">
              {sites.map((site) => {
                const active = String(site.id) === String(selectedSiteId);
                return (
                  <button
                    key={site.id}
                    type="button"
                    className={`w-full text-left rounded-lg border p-2 ${
                      active ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                    onClick={() => {
                      setSelectedSiteId(String(site.id));
                      setSupervisorAssignForm((f) => ({
                        ...f,
                        factory_id: String(site.id),
                        supervisor_user_id: '',
                        employee_user_id: '',
                      }));
                    }}
                  >
                    <p className="font-medium text-slate-900">{site.name}</p>
                    <p className="text-xs text-slate-500">{site.address}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-3 space-y-3 min-w-0">
            <div>
              <h3 className="font-semibold mb-1">Nueva asignación</h3>
              <p className="text-xs text-gray-500 mb-2">
                Elegí un usuario con perfil <strong>Supervisor</strong> (en Empleados) y un empleado de esta sede.
              </p>
              {supervisorCandidates.length === 0 && selectedSiteRows.some((r) => r.active) && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mb-2">
                  No hay supervisores en esta sede. Creá o editá un usuario con perfil Supervisor en Empleados y
                  asignálo a la sede.
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <select
                  className="input-mobile"
                  value={supervisorAssignForm.supervisor_user_id}
                  onChange={(e) => setSupervisorAssignForm((f) => ({ ...f, supervisor_user_id: e.target.value }))}
                  disabled={!selectedSiteId || loading || supervisorCandidates.length === 0}
                >
                  <option value="">
                    {supervisorCandidates.length === 0 ? 'Sin supervisores en esta sede' : 'Supervisor...'}
                  </option>
                  {supervisorCandidates.map((u) => (
                    <option key={`sup-${u.user_id}`} value={u.user_id}>
                      {u.name}
                    </option>
                  ))}
                </select>
                <select
                  className="input-mobile"
                  value={supervisorAssignForm.employee_user_id}
                  onChange={(e) => setSupervisorAssignForm((f) => ({ ...f, employee_user_id: e.target.value }))}
                  disabled={!selectedSiteId || loading}
                >
                  <option value="">Empleado...</option>
                  {employeeCandidates.map((u) => (
                    <option key={`emp-${u.user_id}`} value={u.user_id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="btn-mobile btn-primary w-full mt-2"
                onClick={() =>
                  saveSupervisorAssignment({
                    ...supervisorAssignForm,
                    factory_id: selectedSiteId || '',
                  })
                }
                disabled={
                  loading ||
                  !selectedSiteId ||
                  !supervisorAssignForm.supervisor_user_id ||
                  !supervisorAssignForm.employee_user_id
                }
              >
                Guardar asignación
              </button>
            </div>

            <div className="rounded-xl border border-slate-200 p-2">
              <div className="flex items-center justify-between">
                <p className="font-medium text-sm text-gray-900">
                  {sites.find((s) => String(s.id) === String(selectedSiteId))?.name || 'Sede'}
                </p>
                <span className="text-xs text-gray-500">{rowsForSite.length} asignaciones</span>
              </div>
              {rowsForSite.length === 0 ? (
                <p className="text-xs text-gray-500 mt-1">Sin asignaciones en esta sede.</p>
              ) : (
                <div className="mt-1 space-y-1">
                  {rowsForSite.map((r) => {
                    const sup = employees.find((u) => Number(u.user_id) === Number(r.supervisor_user_id));
                    const emp = employees.find((u) => Number(u.user_id) === Number(r.employee_user_id));
                    return (
                      <div key={r.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2 py-1.5">
                        <p className="text-xs text-slate-700">
                          <span className="font-medium">{sup?.name || `Supervisor ${r.supervisor_user_id}`}</span>
                          <span className="text-slate-400 mx-1" aria-hidden="true">{' • '}</span>
                          <span>{emp?.name || `Empleado ${r.employee_user_id}`}</span>
                        </p>
                        <button
                          type="button"
                          className="btn-mobile btn-danger text-xs py-1 px-2 min-h-0"
                          onClick={() => removeSupervisorAssignment(selectedSiteId, r.id)}
                          disabled={loading}
                        >
                          Quitar
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <AppToast
        message={err || msg}
        variant={err ? 'error' : 'success'}
        onClose={() => {
          setErr('');
          setMsg('');
        }}
      />
    </div>
  );
};

export default OwnerSupervision;
